import {FC, useCallback, useMemo} from "react";
import {makeStyles} from "tss-react/mui";
import {AdminHeader} from "../../AdminHeader";
import {Box, Stack, Switch, Button, Typography, LinearProgress, CircularProgress, Slider} from "@mui/material";
import {useContext, useEffect, useState} from "react";
import {INotificationSeverity, NotificationContext, useApi} from "@buildwitt/shapeshift-identity";
import {deepEqual} from "@buildwitt/shapeshift-common/v1/common";
import {palette} from "@/constants";
import {PageBaseContext} from "@/contexts/pageBaseContext";
import {Add, Delete, Edit} from "@mui/icons-material";
import {ICohortEntity, IPaginatedResponse} from "@buildwitt/shapeshift-types";
import {GridLinkOperator} from "@mui/x-data-grid-pro";
import useDevCycleSdk from "@/hooks/useDevCycleSdk";
import {unstable_useBlocker} from "react-router-dom";
import ConfirmUnsavedChangesDialog from "./ConfirmUnsavedChangesDialog";
import CohortLibraryImportModal from "./CohortLibraryImportModal";
import ConfirmClearEnrollmentsDialog from "./ConfirmClearEnrollmentsDialog";

const useStyles = makeStyles()((theme) => ({
	adminSettingsPage: {
		maxWidth: theme.containers.constrained,
		marginLeft: "auto",
		marginRight: "auto"
	},
	title: {
		margin: "0 10px",
		fontWeight: 700,
		fontSize: "62px"
	},
	importedLibraryTile: {
		display: "flex",
		gap: "10px",
		"& svg path": {
			fill: theme.palette.common.white,
			cursor: "pointer"
		}
	},
	table: {
		borderCollapse: "collapse",
		width: "100%",
		marginTop: theme.spacing(3),
		"td": {
			padding: "20px",
			borderTop: "1px solid " + palette.grey10
		},
		"td:nth-of-type(1) *": {
			fontWeight: 600,
			fontSize: "21px"
		},
		"td:nth-of-type(1)": {
			paddingLeft: 0
		}
	},
	switch: {
		width: 28,
		height: 16,
		padding: 0,
		display: "flex",
		"&:active": {
			"& .MuiSwitch-thumb": {
				width: 15
			},
			"& .MuiSwitch-switchBase.Mui-checked": {
				transform: "translateX(9px)"
			}
		},
		"& .MuiSwitch-switchBase": {
			padding: 2,
			"&.Mui-checked": {
				transform: "translateX(12px)",
				color: "#fff",
				"& + .MuiSwitch-track": {
					opacity: 1
				}
			}
		},
		"& .MuiSwitch-thumb": {
			width: 12,
			height: 12,
			borderRadius: 6,
			transition: theme.transitions.create(["width"], {
				duration: 200
			})
		},
		"& .MuiSwitch-track": {
			borderRadius: 16 / 2,
			opacity: 1,
			boxSizing: "border-box"
		}
	},
	adminSwitch: {
		margin: theme.spacing(3, 0)
	},
	btnContainer: {
		paddingTop: "20px",
		gap: "10px",
		display: "flex",
		"& button": {
			padding: theme.spacing(1, 2),
			width: "auto"
		}
	},
	importedLibCountBadge: {
		padding: "5px 15px",
		borderRadius: "1000px",
		background: theme.palette.primary.main
	},
	importedLibraryTiles: {
		display: "flex",
		padding: "20px 0",
		flexWrap: "wrap",
		gap: "20px",
		"& > div": {
			background: palette.greySolid,
			borderRadius: "5px",
			padding: "20px"
		}
	},
	adminSliders: {
		display: "flex",
		flexGrow: 1,
		flexDirection: "row"
	},
	adminSlider: {
		display: "inline-flex",
		flexFlow: "wrap",
		maxWidth: "170px",
		marginTop: theme.spacing(3),
		marginRight: theme.spacing(3),
		marginBottom: theme.spacing(1.5),
		"& .MuiSlider-thumb": {
			width: 12,
			height: 12
		}
	},
	adminSettingsWrapper: {
		padding: theme.spacing(2, 3, 1),
		background: theme.palette.background.paper,
		borderRadius: "8px",
		border: "1px solid",
		borderColor: palette.grey10
	},
	adminSettingsSection: {
		marginTop: theme.spacing(1.5),
		marginBottom: theme.spacing(3)
	},
	adminSliderInput: {
		marginLeft: theme.spacing(1)
	}
}));

const cohortBaseUrl = "/api/strapi-plugin-shapeshift-lms/cohort";

interface ISettings {
	dailyLimit: number,
	trainingFeedAutoAssignment: boolean,
	clearExistingEnrollments?: boolean,
	comments: boolean,
	courseVariety: number,
	publiclyVisible: boolean,
	extendedAccess?: {id: number, title: string}[],
	isPauseStreakEnabled: boolean,
}

export const AdminSettingsPage: FC = () => {
	const {classes} = useStyles();
	const [{ownedCohort}] = useContext(PageBaseContext);
	const ownedCohortId = (!ownedCohort) ? null : ownedCohort.id;
	const [settings, getSettings, setSettings] = useApi<ISettings>("/api/cohort/" + ownedCohortId + "/settings")
	const [updateUserResponse, updateUser] = useApi<ISettings & {extendedAccess?: {id: number, title: string}[]}>()
	const [formData, setFormData] = useState<ISettings>();
	const {notify} = useContext(NotificationContext)
	const [newCohortLibraryImportModalOpen, setNewCohortLibraryImportModalOpen] = useState(false);
	const changeMade = !deepEqual(formData, settings.data);
	const [stagedCohortSelection, setStagedCohortSelection] = useState<number[]>();
	const [cohortsApi, getCohortsApi] = useApi<IPaginatedResponse<ICohortEntity[]>>();
	const {disableAutoAssign, pauseStreak} = useDevCycleSdk();
	const [confirmUnsavedChangesDialogOpen, setConfirmUnsavedChangesDialogOpen] = useState(false);
	const [confirmClearEnrollmentsDialogOpen, setConfirmClearEnrollmentsDialogOpen] = useState(false);

	useEffect(() => {
		if(!stagedCohortSelection) {
			return
		}

		if(stagedCohortSelection.length === 0) {
			setFormData(prev => ({...prev, extendedAccess: []}));
			return;
		}

		getCohortsApi({
			url: cohortBaseUrl,
			data: {
				page: 1,
				pageSize: 100,
				filters: encodeURIComponent(JSON.stringify({
					operator: GridLinkOperator.And,
					conditions: stagedCohortSelection.map(e => ({attribute: "id", operator: "eq", value: e}))
				}))
			}
		})
	}, [stagedCohortSelection])

	useEffect(() => {
		if(cohortsApi.data){
			setFormData(prev => ({...prev, extendedAccess: cohortsApi.data?.data?.map(e => ({id: e.id, title: e.title}))}));
		}
	}, [cohortsApi.data])

	useEffect(() => {
		const ownedCohortId = (!ownedCohort) ? null : ownedCohort.id;
		if(ownedCohortId){
			getSettings("/api/cohort/" + ownedCohortId + "/settings")
		}
	}, [ownedCohort])

	useEffect(() => {
		if(settings.data){
			setFormData({
				...settings.data,
				trainingFeedAutoAssignment: (settings.data.trainingFeedAutoAssignment === undefined || settings.data.trainingFeedAutoAssignment === null)
					? true
					: settings.data.trainingFeedAutoAssignment
			});
		}
	}, [settings])

	useEffect(() => {
		if(updateUserResponse.statusCode === 200){
			notify("Your details have been updated", "Success", INotificationSeverity.success, 5000)
			setSettings(formData)
		}
	}, [updateUserResponse])

	const handleUpdate = useCallback((): void => {
		if(updateUserResponse.isLoading || !changeMade)
			return;

		const payload = {
			...formData,
			extendedAccess: formData.extendedAccess
		}
		updateUser({
			url: "/api/cohort/" + ownedCohortId + "/settings",
			data: payload,
			method: "PUT"
		})
	}, [updateUserResponse, formData, changeMade, ownedCohortId]);

	const handleSavePreferences = useCallback(() => {
		if(updateUserResponse.isLoading || !changeMade)
			return;
		if(formData?.clearExistingEnrollments){
			setConfirmClearEnrollmentsDialogOpen(true);
		}
		else{
			handleUpdate();
		}
	}, [handleUpdate, updateUserResponse.isLoading, changeMade, formData]);


	const formDataRequiresSave = useMemo(() => {
		return !deepEqual(formData, settings.data);
	}, [formData, settings.data]);

	const blocker = unstable_useBlocker(formDataRequiresSave);

	useEffect(() => {
		if (blocker.state === "blocked") {
			setConfirmUnsavedChangesDialogOpen(true);
		}}, [blocker]);

	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent): any => {
			const noChangeToState = deepEqual(formData, settings.data);
			if(noChangeToState)
				return false;

			event.preventDefault();
			event.returnValue = ""; // For older browsers
			return true;
		};

		//catch page refresh, close or external navigation
		window.addEventListener("beforeunload", handleBeforeUnload);

		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, [formData, settings.data]);

	return (
		<Box className={classes.adminSettingsPage}>
			<AdminHeader title="Settings" subtitle="Manage settings" />
			{(!settings.data || settings.isLoading || !formData) ? (
				<LinearProgress />
			) : (
				<Box className={classes.adminSettingsWrapper}>
					<Box className={classes.adminSettingsSection}>
						<Typography variant="h4">My Feed Content</Typography>
						{disableAutoAssign && <Stack sx={{marginTop: "1px"}} direction="row" paddingTop={2} spacing={1} alignItems="center">
							<Switch className={classes.switch} checked={!formData.trainingFeedAutoAssignment} onChange={e => setFormData(prev => ({...prev, trainingFeedAutoAssignment: !e.target.checked}))}/>
							<Typography>Disable auto assignment</Typography>
						</Stack>}

						{(disableAutoAssign && !formData.trainingFeedAutoAssignment) && <Stack sx={{marginTop: "1px"}} direction="row" paddingTop={2} spacing={1} alignItems="center">
							<Switch className={classes.switch} checked={formData.clearExistingEnrollments} onChange={e => setFormData(prev => ({...prev, clearExistingEnrollments: e.target.checked}))}/>
							<Typography>Clear existing auto-enrollments</Typography>
						</Stack>}

						{pauseStreak && <Stack sx={{marginTop: "1px"}} direction="row" paddingTop={2} spacing={1} alignItems="center">
							<Switch className={classes.switch} checked={formData.isPauseStreakEnabled} onChange={e => setFormData(prev => ({...prev, isPauseStreakEnabled: e.target.checked}))}/>
							<Typography>Allow employees to pause their streak </Typography>
						</Stack>}

						<Box className={classes.adminSliders}>
							<Box className={classes.adminSlider}>
								<Typography>Daily assignment</Typography>
								<Slider
									className={classes.adminSliderInput}
									defaultValue={1}
									step={1}
									max={3}
									min={1}
									value={formData.dailyLimit}
									onChange={(_e, value) => setFormData(prev => ({...prev, dailyLimit: value as number}))}
									valueLabelDisplay="auto"
									getAriaValueText={e => e.toString()}
									marks={([1, 2, 3]).map(e => ({value: e, label: e.toString()}))}
								/>
							</Box>
							{/*
							<Box className={classes.adminSlider}>
								<Typography>Course Variety</Typography>
								<Slider
									className={classes.adminSliderInput}
									defaultValue={4}
									step={1}
									max={6}
									min={1}
									value={formData.courseVariety}
									onChange={(_e, value) =>
										setFormData(prev => ({...prev, courseVariety: value as number}))
									}
									valueLabelDisplay="auto"
									getAriaValueText={e => e.toString()}
									marks={([1, 2, 3, 4, 5, 6]).map(e => ({
										value: e,
										label: e.toString()
									}))}
								/>
							</Box>
							*/}
						</Box>

					</Box>
					<Box className={classes.adminSettingsSection}>
						<Typography variant="h4">Privacy and Content Sharing</Typography>
						<Box className={classes.adminSwitch} sx={{display: "flex", gap: "25px"}}>
							<Stack sx={{marginTop: "1px"}} direction="row" spacing={1} alignItems="center">
								<Switch className={classes.switch} checked={formData.publiclyVisible} onChange={e => setFormData(prev => ({...prev, publiclyVisible: e.target.checked}))}/>
								<Typography>Publicly Visible</Typography>
							</Stack>
						</Box>
						<Typography variant="h4">Comments</Typography>
						<Box className={classes.adminSwitch} sx={{display: "flex", gap: "25px"}}>
							<Stack sx={{marginTop: "1px"}} direction="row" spacing={1} alignItems="center">
								<Switch className={classes.switch} checked={!!formData.comments} onChange={e => setFormData(prev => ({...prev, comments: e.target.checked}))}/>
								<Typography>Enabled</Typography>
							</Stack>
						</Box>
						<Box sx={{display: "flex", gap: "10px"}}>
							<Typography>Imported Libraries: </Typography>
							{((formData.extendedAccess?.length ?? 0) == 0) && <Typography sx={{fontWeight: 600}}>none</Typography>}
						</Box>
						<Box className={classes.importedLibraryTiles}>
							{formData.extendedAccess?.map(e => <Box className={classes.importedLibraryTile} key={e.id}>
								<Typography>{e.title}</Typography>
								<Delete onClick={() => setFormData(prev => ({...prev, extendedAccess: prev.extendedAccess?.filter(x => e.id !== x.id)}))}/></Box>
							)}
						</Box>
						<Button variant="contained" onClick={() => setNewCohortLibraryImportModalOpen(true)} sx={{marginTop: "20px"}}>
							{(formData.extendedAccess?.length ?? 0) > 0 ? "Modify Selection" : "Add Library"}
							{(formData.extendedAccess?.length ?? 0) > 0 ? <Edit /> : <Add />}
						</Button>
						<Box padding={2}></Box>
						<Box className={classes.btnContainer}>
							<Button variant="contained" disabled={!changeMade} sx={{width: "190px"}} onClick={() => handleSavePreferences()}>
								{updateUserResponse.isLoading ? <CircularProgress /> : "Save preferences"}
							</Button>
							{settings.data && <Button variant="contained" disabled={!changeMade} onClick={() => settings.data && setFormData(settings.data)}>Cancel changes</Button>}
						</Box>

					</Box>
				</Box>
			)}
			{newCohortLibraryImportModalOpen && <CohortLibraryImportModal
				currentSelected={formData.extendedAccess?.map(e => e.id)}
				onClose={(e) => {
					setStagedCohortSelection(e);
					setNewCohortLibraryImportModalOpen(false);
				}}
			/>}
			{confirmUnsavedChangesDialogOpen
				&& <ConfirmUnsavedChangesDialog
					onAccept={() =>{blocker.proceed()}}
					onClose={() => {setConfirmUnsavedChangesDialogOpen(false); blocker.reset();}}
				/>
			}
			{confirmClearEnrollmentsDialogOpen
				&& <ConfirmClearEnrollmentsDialog
					onAccept={() => {
						setConfirmClearEnrollmentsDialogOpen(false);
						handleUpdate();
					}}
					onClose={() => setConfirmClearEnrollmentsDialogOpen(false)}
				/>
			}
		</Box>
	);
}